import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import { registrarAcao } from '@/shared/utils/reportAction';
import { reportError } from '@/shared/utils/reportError';

import type { ErroDaConsultaDaConversao, ResultadoDaConversao } from '../../types/conversao.types';
import {
  consultarConversaoDaNotificacao,
  FalhaDaConsultaDaConversao,
  MENSAGEM_DE_FALHA_DA_CONVERSAO,
  STATUS_FORA_DO_CONTRATO,
} from './services';

const ORIGEM_DA_TELEMETRIA = 'extrajudicial.conversao.consultar';

const paraErroDaConsulta = (erro: unknown): ErroDaConsultaDaConversao =>
  erro instanceof FalhaDaConsultaDaConversao
    ? erro.erro
    : { status: STATUS_FORA_DO_CONTRATO, mensagem: MENSAGEM_DE_FALHA_DA_CONVERSAO };

/**
 * 4xx é recusa prevista do core (item excluído, fora do público, sessão): vai como ação, não como
 * erro, para não misturar com a falha de rede, 5xx ou contrato quebrado que alguém precisa olhar.
 */
function registrarFalha(itemId: string, erro: unknown, falha: ErroDaConsultaDaConversao): void {
  if (falha.status >= 400 && falha.status < 500) {
    registrarAcao('extrajudicial.conversao.recusada', { itemId, status: falha.status });
    return;
  }
  const causa = erro instanceof Error ? erro : new Error(MENSAGEM_DE_FALHA_DA_CONVERSAO);
  reportError(causa, { source: ORIGEM_DA_TELEMETRIA, itemId, status: falha.status });
}

/**
 * Sempre vai ao core, inclusive depois de um `SEM_CONVERSAO`: sem cache negativo, a conversão
 * registrada depois (ou a origem identificada depois) aparece na volta ao Resumo geral. O
 * `requestId` desta chamada é o `consultaId` do estado; o reducer descarta resposta de consulta
 * que não é mais a atual. Cancelada pelo `abort()` da promessa do dispatch.
 */
export const consultarConversao = createAsyncThunk<
  ResultadoDaConversao,
  string,
  { rejectValue: ErroDaConsultaDaConversao }
>('extrajudicialConversao/consultar', async (itemId, { rejectWithValue, signal }) => {
  try {
    return await consultarConversaoDaNotificacao(itemId, signal);
  } catch (erro) {
    const falha = paraErroDaConsulta(erro);
    if (!signal.aborted) registrarFalha(itemId, erro, falha);
    return rejectWithValue(falha);
  }
});

export const limparConsultaDaConversao = createAction<string>(
  'extrajudicialConversao/limparConsulta',
);
